import React, { useState } from "react";
import styles from "../../css/SmsDownloadReport.module.css";
import { useForm } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import CustomAlert from "../../helpers/CustomAlert";
import dayjs from "dayjs";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

const defaultFormData = {
  lastMonth: "",
  last3Months: "",
  last6Months: "",
  lastYear: "",
  startDate: "",
  endDate: "",
};
const TrafficReportStatement = ({
  handleopenReport,
  handleCloseReport,
  callApi,
  loading,
}) => {
  const { handleSubmit, watch, setValue } = useForm({
    defaultValues: defaultFormData,
    mode: "onChange",
  });

  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));

  const operatorSelected = watch("operator", []);

  if (!handleopenReport) return null;

  const operatorOptions = [
    { value: "jio", label: "Jio" },
    { value: "onextel", label: "Onextel" },
    { value: "smartping", label: "Smartping" },
  ];

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    // Clear custom dates when predefined period is selected
    if (period !== "custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  const onSubmit = () => {
    let fromDate = "";
    let toDate = "";
    const today = dayjs();
    const format = "DD-MM-YYYY";

    // If startDate and endDate are valid and period is custom, use them
    if (
      startDate &&
      endDate &&
      dayjs(startDate).isValid() &&
      dayjs(endDate).isValid() &&
      selectedPeriod === "custom"
    ) {
      fromDate = dayjs(startDate).format(format);
      toDate = dayjs(endDate).format(format);
    } else {
      switch (selectedPeriod) {
        case "lastMonth":
          fromDate = today.subtract(1, "month").startOf("month").format(format);
          toDate = today.subtract(1, "month").endOf("month").format(format);
          break;
        case "last3Months":
          fromDate = today.subtract(3, "month").startOf("month").format(format);
          toDate = today.endOf("month").format(format);
          break;
        case "last6Months":
          fromDate = today.subtract(6, "month").startOf("month").format(format);
          toDate = today.endOf("month").format(format);
          break;
        case "lastYear":
          fromDate = today.subtract(1, "year").startOf("month").format(format);
          toDate = today.endOf("month").format(format);
          break;
        default:
          // If no period is selected but valid dates are provided, treat as custom
          if (
            startDate &&
            endDate &&
            dayjs(startDate).isValid() &&
            dayjs(endDate).isValid()
          ) {
            // Validate that endDate is not before startDate
            if (dayjs(endDate).isBefore(dayjs(startDate))) {
              CustomAlert({
                title: "Error",
                text: "End date cannot be earlier than start date.",
                icon: "warning",
                confirmButtonText: "OK",
              });
              return;
            }
            fromDate = dayjs(startDate).format(format);
            toDate = dayjs(endDate).format(format);
          } else {
            CustomAlert({
              title: "Error",
              text: "Please select a valid date range or provide valid start and end dates.",
              icon: "warning",
              confirmButtonText: "OK",
            });
            return;
          }
      }
    }

    callApi(fromDate, toDate, operatorSelected, "Traffic & Usage");
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.modalOverlay}>
        <div className={styles.modalBoxStatement}>
          {/* Header */}
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>Traffic Report</h2>
            <button onClick={handleCloseReport} className={styles.closeButton}>
              <HighlightOffIcon />
            </button>
          </div>

          {/* Content */}
          <div className={styles.modalContent}>
            <h3 className={styles.questionTitle}>
              For which period do you need a statement?
            </h3>

            {/* Period Options */}
            <div className={styles.periodSection}>
              <div className={styles.periodGrid}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="period"
                    value="lastMonth"
                    checked={selectedPeriod === "lastMonth"}
                    onChange={() => handlePeriodChange("lastMonth")}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Last Month</span>
                </label>

                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="period"
                    value="last3Months"
                    checked={selectedPeriod === "last3Months"}
                    onChange={() => handlePeriodChange("last3Months")}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Last 3 Months</span>
                </label>

                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="period"
                    value="last6Months"
                    checked={selectedPeriod === "last6Months"}
                    onChange={() => handlePeriodChange("last6Months")}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Last 6 Months</span>
                </label>

                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="period"
                    value="lastYear"
                    checked={selectedPeriod === "lastYear"}
                    onChange={() => handlePeriodChange("lastYear")}
                    className={styles.radioInput}
                  />
                  <span className={styles.radioText}>Last 1 Year</span>
                </label>
              </div>
            </div>

            {/* Custom Date Section */}
            <div className={styles.customDateSection}>
              <p className={styles.customDateTitle}>
                Or select a custom date of your choice
              </p>

              <div className={styles.dateGrid}>
                <div className={styles.dateField}>
                  <label className={styles.dateLabel}>Start Date</label>
                  <div className={styles.dateInputWrapper}>
                    <input
                      type="date"
                      value={startDate}
                      max={(() => {
                        const today = new Date().toISOString().split("T")[0];
                        const toDate = endDate;
                        return toDate && toDate < today ? toDate : today;
                      })()}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setSelectedPeriod("custom");
                      }}
                      className={styles.dateInput}
                    />
                  </div>
                </div>

                <div className={styles.dateField}>
                  <label className={styles.dateLabel}>End Date</label>
                  <div className={styles.dateInputWrapper}>
                    <input
                      type="date"
                      value={endDate}
                      min={startDate}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setSelectedPeriod("custom");
                      }}
                      className={styles.dateInput}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Operator Selection */}
            <div className={styles.customDateSection}>
              <p className={styles.customDateTitle}>Or Select Operators</p>

              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  flexWrap: "wrap",
                  padding: "0.75rem",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #ced4da",
                  borderRadius: "0.5rem",
                }}
              >
                {operatorOptions.map((operator) => {
                  const checked = operatorSelected.includes(operator.value);
                  return (
                    <label
                      key={operator.value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.5rem 0.75rem",
                        borderRadius: "0.375rem",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: "500",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const newOperator = e.target.checked
                            ? [...operatorSelected, operator.value]
                            : operatorSelected.filter(
                                (v) => v !== operator.value
                              );

                          setValue("operator", newOperator);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      {operator.label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div
              className="scoped-modal"
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px",
                paddingBottom: "0px",
                gap: "16px",
                borderTop: "1px solid #dee2e6",
              }}
            >
              <button
                type="button"
                className="btn btn-back"
                onClick={() => {
                  handleCloseReport();
                }}
              >
                Back
              </button>
              <div className={styles.buttonContainer}>
                <button type="submit" className="btn btn-submit">
                  {loading ? (
                    <CircularProgress
                      style={{ width: "20px", height: "20px" }}
                    />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default TrafficReportStatement;
